import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  getAgentDirectory,
  getAgentDirectorySuggestions,
  getBrokerage,
  getDirectoryBrokerages,
  getDirectoryPlace,
  getDirectoryState,
  getDirectoryStates,
  resolveDirectoryPlace,
} from "@/services/agentDirectoryService";

const resultOf = (response, fallback) => {
  if (response?.errorCode !== 0) throw new Error(response?.message || fallback);
  return response.result;
};

export const isNotFound = (error) => error?.response?.status === 404 || error?.notFound === true;

const retryUnlessMissing = (count, error) => !isNotFound(error) && count < 2;

export const useAgentDirectory = (location, { withBuyers = false, q = "", brokerage = "", network = "" } = {}, place = "") => {
  const lat = location?.latitude;
  const long = location?.longitude;
  return useInfiniteQuery({
    queryKey: ["agentDirectory", lat, long, place, withBuyers, q, brokerage, network],
    queryFn: async ({ pageParam }) =>
      resultOf(
        await getAgentDirectory({ lat, long, place, page: pageParam, withBuyers, q, brokerage, network }),
        "Could not load agents.",
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last?.hasMore ? last.page + 1 : undefined),
    enabled: Boolean(lat && long),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
};

export const useAgentSuggestions = (q, location) => {
  const text = String(q || "").trim();
  const lat = location?.latitude;
  const long = location?.longitude;
  return useQuery({
    queryKey: ["agentDirectorySuggest", text.toLowerCase(), lat, long],
    queryFn: async () => resultOf(await getAgentDirectorySuggestions(text, lat, long), "Could not load suggestions."),
    enabled: text.length >= 2,
    // Only carry old suggestions across keystrokes while a query is active.
    placeholderData: text.length >= 2 ? keepPreviousData : undefined,
    staleTime: 60 * 1000,
  });
};

export const useBrokerage = (name, network = "", q = "") => {
  const text = String(q || "").trim();
  return useInfiniteQuery({
    queryKey: ["brokerage", name, network, text.toLowerCase()],
    queryFn: async ({ pageParam }) =>
      resultOf(await getBrokerage({ name, network, page: pageParam, q: text }), "Could not load this brokerage."),
    initialPageParam: 1,
    getNextPageParam: (last) => (last?.hasMore ? last.page + 1 : undefined),
    enabled: Boolean(name),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
};

const DAY_MS = 24 * 60 * 60 * 1000;

export const useDirectoryStates = () =>
  useQuery({
    queryKey: ["directoryStates"],
    queryFn: async () => resultOf(await getDirectoryStates(), "Could not load states."),
    staleTime: DAY_MS,
  });

export const useDirectoryState = (state) =>
  useQuery({
    queryKey: ["directoryState", String(state || "").toLowerCase()],
    queryFn: async () => resultOf(await getDirectoryState(state), "Could not load this state."),
    enabled: Boolean(state),
    retry: retryUnlessMissing,
    staleTime: DAY_MS,
  });

export const useDirectoryPlace = (state, town) =>
  useQuery({
    queryKey: ["directoryPlace", String(state || "").toLowerCase(), String(town || "").toLowerCase()],
    queryFn: async () => resultOf(await getDirectoryPlace(state, town), "Could not load this town."),
    enabled: Boolean(state && town),
    retry: retryUnlessMissing,
    staleTime: DAY_MS,
  });

export const useDirectoryBrokerages = (location, q = "", enabled = true) => {
  const lat = location?.latitude;
  const long = location?.longitude;
  const text = String(q || "").trim();
  return useInfiniteQuery({
    queryKey: ["directoryBrokerages", lat, long, text.toLowerCase()],
    queryFn: async ({ pageParam }) =>
      resultOf(await getDirectoryBrokerages({ lat, long, q: text, page: pageParam }), "Could not load brokerages."),
    initialPageParam: 1,
    getNextPageParam: (last, all) => (last?.hasMore ? all.length + 1 : undefined),
    enabled: Boolean(enabled && lat && long),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
};

export const useResolvePlace = (location) => {
  const lat = location?.latitude;
  const long = location?.longitude;
  return useQuery({
    queryKey: ["directoryResolve", lat, long],
    queryFn: async () => resultOf(await resolveDirectoryPlace(lat, long), "Could not find a town here."),
    enabled: Boolean(lat && long),
    retry: false,
    staleTime: DAY_MS,
  });
};
